import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Wallet,
  Copy,
  ExternalLink,
  Activity,
  TrendingUp,
  TrendingDown,
  Clock,
  ArrowUpRight,
  ArrowDownLeft,
  Shield,
  DollarSign,
  BarChart3,
  Users,
  Zap,
  X
} from 'lucide-react';

interface AddressInfo {
  address: string;
  balance: string;
  vttnBalance: string;
  usdtBalance: string;
  transactionCount: number;
  firstSeen: string;
  lastSeen: string;
  type: 'wallet' | 'contract' | 'validator';
  isContract: boolean;
  contractName?: string;
}

interface Transaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  gasPrice: string;
  gasUsed: string;
  timestamp: string;
  status: 'success' | 'failed';
  type: 'transfer' | 'contract' | 'staking';
}

const AddressPage: React.FC = () => {
  const { address } = useParams<{ address: string }>();
  const navigate = useNavigate();
  const [addressInfo, setAddressInfo] = useState<AddressInfo | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('transactions');
  const [currentPage, setCurrentPage] = useState(1);
  const transactionsPerPage = 20;

  useEffect(() => {
    if (address) {
      fetchAddressInfo();
    }
  }, [address]);

  const fetchAddressInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 실제 API 호출
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      
      try {
        // 어드레스 정보와 트랜잭션 조회
        const [addressResponse, txResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/explorer/addresses/${address}`),
          fetch(`${apiBaseUrl}/explorer/addresses/${address}/transactions?limit=50`)
        ]);
        
        if (addressResponse.ok && txResponse.ok) {
          const addressData = await addressResponse.json();
          const txData = await txResponse.json();
          
          console.log('🔍 어드레스 API 응답:', { addressData, txData });
          
          if (addressData.success && txData.success) {
            // 어드레스 정보 매핑
            const addrInfo = addressData.address || addressData;
            console.log('✅ 어드레스 데이터:', {
              address: addrInfo.address || address,
              balance: addrInfo.balance,
              transactions: txData.transactions?.length
            });
            const addressInfo: AddressInfo = {
              address: address || '',
              balance: parseFloat(addrInfo.balance || '0').toLocaleString(),
              vttnBalance: parseFloat(addrInfo.balance || '0').toLocaleString(),
              usdtBalance: '0',
              transactionCount: parseInt(addrInfo.transactionCount || txData.transactions?.length || '0'),
              firstSeen: addrInfo.firstSeen || new Date().toISOString(),
              lastSeen: addrInfo.lastSeen || new Date().toISOString(),
              type: addrInfo.isContract ? 'contract' : 'wallet',
              isContract: addrInfo.isContract || false
            };
            
            // 트랜잭션 매핑 (value가 0인 것 제외)
            const transactions: Transaction[] = (txData.transactions || [])
              .filter((tx: any) => parseFloat(tx.value || '0') > 0) // value가 0보다 큰 것만
              .map((tx: any) => ({
                hash: tx.hash,
                from: tx.from,
                to: tx.to,
                value: parseFloat(tx.value || '0').toFixed(3),
                gasPrice: parseFloat(tx.gasPrice || '0') / 1e9, // Gwei
                gasUsed: parseInt(tx.gasUsed || '0'),
                timestamp: typeof tx.timestamp === 'number' 
                  ? new Date(tx.timestamp * 1000).toISOString() 
                  : new Date(tx.timestamp).toISOString(),
                status: tx.status || 'pending',
                type: tx.to ? 'transfer' : 'contract'
              }));
            
            setAddressInfo(addressInfo);
            setTransactions(transactions);
            setLoading(false);
            return;
          }
        }
      } catch (error) {
        console.error('API 호출 실패:', error);
        setError('주소 정보를 불러오는데 실패했습니다. 주소가 올바른지 확인해주세요.');
      }
      
      // API 실패 시 에러만 표시 (Mock 데이터 사용 안 함)
      if (!addressInfo) {
        setError('주소 정보를 불러오는데 실패했습니다. 주소가 올바른지 확인해주세요.');
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch address info:', error);
      setLoading(false);
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'transfer': return 'bg-blue-100 text-blue-800';
      case 'contract': return 'bg-purple-100 text-purple-800';
      case 'staking': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const paginatedTransactions = transactions.slice(
    (currentPage - 1) * transactionsPerPage,
    currentPage * transactionsPerPage
  );

  const totalPages = Math.ceil(transactions.length / transactionsPerPage);

  if (loading) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      </motion.div>
    );
  }

  if (!addressInfo) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        <div className="text-center py-20">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Address Not Found</h1>
          <p className="text-gray-600">The address you're looking for doesn't exist or is invalid.</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {/* 헤더 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="h-16 w-16 rounded-full bg-gradient-to-r from-orange-400 to-red-500 flex items-center justify-center">
              <Wallet className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Address Details</h1>
              <div className="flex items-center space-x-2 mt-1">
                <span className="font-mono text-gray-600">{formatAddress(addressInfo.address)}</span>
                <button
                  onClick={() => copyToClipboard(addressInfo.address)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  addressInfo.type === 'validator' ? 'bg-purple-100 text-purple-800' :
                  addressInfo.type === 'contract' ? 'bg-blue-100 text-blue-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {addressInfo.type}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* 주소 전체 표시 */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <span className="font-mono text-sm text-gray-900 break-all">{addressInfo.address}</span>
            <button
              onClick={() => copyToClipboard(addressInfo.address)}
              className="ml-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">VTTN Balance</p>
              <p className="text-2xl font-bold text-gray-900">{addressInfo.vttnBalance}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">USDT Balance</p>
              <p className="text-2xl font-bold text-gray-900">{addressInfo.usdtBalance}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Wallet className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{addressInfo.transactionCount.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Last Activity</p>
              <p className="text-2xl font-bold text-gray-900">{formatTimeAgo(addressInfo.lastSeen)}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('transactions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'transactions'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Transactions ({transactions.length})
            </button>
            <button
              onClick={() => setActiveTab('tokens')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tokens'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Token Holdings
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'transactions' && (
            <div className="space-y-4">
              {paginatedTransactions.map((tx) => (
                <div key={tx.hash} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-lg ${
                      tx.from.toLowerCase() === addressInfo.address.toLowerCase() ? 'bg-red-100' : 'bg-green-100'
                    }`}>
                      {tx.from.toLowerCase() === addressInfo.address.toLowerCase() ? (
                        <ArrowUpRight className="w-4 h-4 text-red-600" />
                      ) : (
                        <ArrowDownLeft className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/transactions/${tx.hash}`)}
                          className="font-mono text-sm text-blue-600 hover:text-blue-800 underline"
                        >
                          {tx.hash.slice(0, 10)}...{tx.hash.slice(-8)}
                        </button>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(tx.type)}`}>
                          {tx.type}
                        </span>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          tx.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {tx.status}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                        <span>From: {formatAddress(tx.from)}</span>
                        <span>To: {formatAddress(tx.to)}</span>
                        <span>{formatTimeAgo(tx.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-medium ${
                      tx.from.toLowerCase() === addressInfo.address.toLowerCase() ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {tx.from.toLowerCase() === addressInfo.address.toLowerCase() ? '-' : '+'}{tx.value} VTTN
                    </div>
                    <div className="text-xs text-gray-500">
                      Gas: {tx.gasUsed} ({tx.gasPrice} gwei)
                    </div>
                  </div>
                </div>
              ))}
              
              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                    if (page > totalPages) return null;
                    
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 border rounded-lg ${
                          currentPage === page
                            ? 'bg-orange-500 text-white border-orange-500'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'tokens' && (
            <div className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">Token Holdings</h3>
              <p className="text-gray-500">Token balance information will be displayed here.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* 액션 버튼 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex justify-center space-x-4">
          <button
            onClick={() => navigate('/blocks')}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <BarChart3 className="w-4 h-4" />
            <span>블록 탐색기</span>
          </button>
          <button
            onClick={() => navigate('/transactions')}
            className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <Activity className="w-4 h-4" />
            <span>트랜잭션 탐색기</span>
          </button>
          <button
            onClick={() => navigate('/validators')}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
          >
            <Shield className="w-4 h-4" />
            <span>검증자 보기</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default AddressPage;
